import React, { FC, Fragment } from 'react';
import { Trans } from 'react-i18next';
import { compose } from 'recompose';
import { Button, Divider, Grid, List, Paper, Typography } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';

import { Network, SocialAccount } from 'api';
import { CustomSVG, Numeric } from 'components';
import { useCurrentUser } from 'hooks';
import { Account } from 'Links';

import Item from './Reach/Item';
import ClientFollowers from './Reach/ClientFollowers';
import ConnectTwitter from './Reach/ConnectTwitter';
import ConnectLinkedIn from './Reach/ConnectLinkedIn';

import { IWithSocialConnections, withSocialConnections } from 'providers/queries';

interface IPropsEnhanced extends WithStyles<typeof styles>, IWithSocialConnections {}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: '16px 16px 0',
    },
    icon: {
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
    },
    reach: {
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: theme.typography.pxToRem(28),
      fontWeight: theme.typography.fontWeightBold,
    },
    list: {
      padding: '8px 0',
    },
    perNetworkReach: {
      color: theme.custom.palette.grey1,
    },
    button: {
      padding: '8px 0',
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(withSocialConnections('widget'), withStyles(styles));

const TotalReach: FC<IPropsEnhanced> = ({ classes, connections }) => {
  const { follower_count, reach } = useCurrentUser();

  function renderItem(account: SocialAccount) {
    return <Item key={account.id} account={account} />;
  }

  function renderList() {
    const twitterStats = connections.filter(conn => conn.provider === Network.Twitter);
    const linkedInStats = connections.filter(conn => conn.provider === Network.Linkedin);

    return (
      <List className={classes.list}>
        <ClientFollowers />
        {twitterStats.length ? twitterStats.map(renderItem) : <ConnectTwitter />}
        {linkedInStats.length ? linkedInStats.map(renderItem) : <ConnectLinkedIn />}
      </List>
    );
  }

  return (
    <Paper className={classes.root}>
      <Typography variant="subtitle2">
        <Trans i18nKey="user:myTotalReach" />
      </Typography>
      <Grid container alignItems="center" justify="space-between">
        <Grid item>
          <Typography className={classes.reach}>
            <CustomSVG className={classes.icon} name="network" />
            <Numeric value={follower_count + reach} />
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      {renderList()}
      {!!connections.length && (
        <Fragment>
          <Divider />
          <Button component={Account} fullWidth className={classes.button}>
            <Trans i18nKey="sharing:connectMore" />
          </Button>
        </Fragment>
      )}
    </Paper>
  );
};

export default enhancers(TotalReach);
